/**
 * useSlideoutDismissGuard - Router-free close-intent guard for slideouts.
 *
 * A strict subset of `useUnsavedChangesGuard` with every router-blocking
 * reference removed, so slideouts using this hook do not require a
 * react-router data router. Provides:
 * - Browser close/refresh protection via beforeunload (opt-in)
 * - Guard function for custom dismissal triggers (close buttons, etc.)
 * - Modal props for integration with UnsavedChangesModal (3-button: Save / Discard / Cancel)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { logger } from '@/lib/logger';

import type { UnsavedChangesModalProps } from './unsaved-changes-modal';

// Re-exported so consumers that read the modal-props type from this hook
// don't need a separate import from the modal module.
export type { UnsavedChangesModalProps };

/** Bound on how long a save may hang before the guard gives up and lets the user retry. */
const SAVE_TIMEOUT_MS = 30_000;

/** Distinguishes a hung `onSave()` from a rejected one, for logging only. */
class SaveTimeoutError extends Error {
  constructor() {
    super('useSlideoutDismissGuard: onSave() timed out');
  }
}

/**
 * Options for useSlideoutDismissGuard hook.
 */
export interface UseSlideoutDismissGuardOptions {
  /** Whether there are unsaved changes to protect */
  hasUnsavedChanges: boolean;
  /** Callback to discard changes when user confirms leaving */
  onDiscard?: () => void;
  /** Callback to save changes. When provided, the modal shows a "Save Changes" button. */
  onSave?: () => Promise<void>;
  /** Whether to block browser close/refresh (default: false) */
  blockBrowserClose?: boolean;
}

/**
 * Result returned by useSlideoutDismissGuard hook.
 */
export interface UseSlideoutDismissGuardResult {
  /**
   * Guard function that blocks action if unsaved changes exist. Returns true
   * if action should proceed.
   *
   * The optional second parameter overrides `hasUnsavedChanges` for this call
   * only, falling back to the hook's own `hasUnsavedChanges` when omitted.
   * This lets one guard instance gate two differently-scoped actions (e.g.
   * closing just the top entry vs. clearing an entire stack) without a second
   * guard instance or a second `UnsavedChangesModal`.
   */
  guard: (action: () => void, hasUnsavedChangesOverride?: boolean) => boolean;
  /** Props to spread to UnsavedChangesModal */
  modalProps: UnsavedChangesModalProps;
  /**
   * Whether the confirm-prompt modal is currently open. This does NOT
   * reflect router navigation blocking — this hook never blocks navigation,
   * it only guards explicit dismissal actions passed to `guard`.
   */
  isBlocking: boolean;
}

/**
 * Router-free hook for protecting against accidental slideout dismissal with
 * unsaved changes.
 *
 * @example
 * ```tsx
 * const { guard, modalProps, isBlocking } = useSlideoutDismissGuard({
 *   hasUnsavedChanges,
 *   onDiscard: resetChanges,
 *   onSave: saveChanges,
 * });
 *
 * const handleClose = () => {
 *   if (guard(onClose)) {
 *     onClose();
 *   }
 * };
 *
 * return (
 *   <>
 *     <YourComponent />
 *     <UnsavedChangesModal {...modalProps} />
 *   </>
 * );
 * ```
 */
export function useSlideoutDismissGuard({
  hasUnsavedChanges,
  onDiscard,
  onSave,
  blockBrowserClose = false,
}: UseSlideoutDismissGuardOptions): UseSlideoutDismissGuardResult {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);
  // The hasUnsavedChanges override (if any) that gated the currently pending
  // action, so the auto-close effect below re-checks the same scope that
  // opened the modal rather than the hook's own base `hasUnsavedChanges`.
  const pendingOverrideRef = useRef<boolean | undefined>(undefined);
  // Element focused when the guard opened the modal (the Close button, in
  // practice). Every dismissal path leaves the slideout's own contain:true
  // FocusScope mounted, so react-aria's unmount-time restore (a
  // requestAnimationFrame that only fires while document.activeElement is
  // still <body>) races the slideout's blur-containment rAF to reclaim
  // focus onto its panel. Whichever wins is arbitrary — see
  // `restoreFocusTargetRef` below for the deterministic fix.
  const triggerElementRef = useRef<HTMLElement | null>(null);
  // Set on every dismissal (Cancel, Discard, Save) and consumed by the effect
  // below once the modal has actually unmounted.
  const restoreFocusTargetRef = useRef<HTMLElement | null>(null);

  // Block browser close/refresh with native dialog
  useEffect(() => {
    if (!blockBrowserClose || !hasUnsavedChanges) {
      return;
    }

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [blockBrowserClose, hasUnsavedChanges]);

  // Execute the pending action and clean up. setIsModalOpen(false) happens
  // BEFORE invoking the action so the modal's FocusScope restores focus to
  // the triggering control before that control's action runs. Also records
  // the triggering control for the deterministic restore-focus effect below,
  // the same as handleCancel — otherwise Discard/Save are left racing
  // react-aria's own restoreFocus against the slideout's FocusScope
  // reclaiming focus onto its panel.
  const executePendingAction = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    pendingOverrideRef.current = undefined;
    restoreFocusTargetRef.current = triggerElementRef.current;
    triggerElementRef.current = null;
    setIsModalOpen(false);

    if (action) {
      action();
    }
  }, []);

  // Auto-close modal and execute pending action if blocking condition clears while modal is open.
  // This handles the case where a save triggered from the modal completes successfully -
  // hasUnsavedChanges becomes false, so we should proceed with the originally-requested action
  // (e.g., closing the slideout) rather than just closing the modal.
  //
  // Re-checks the same scope that opened the modal (pendingOverrideRef, falling
  // back to the base hasUnsavedChanges) rather than the base value alone — a
  // guard() call made with an override (e.g. requestClear's "any entry dirty"
  // scope) must not be auto-resolved just because the unrelated base scope
  // (e.g. "is the top entry dirty") happens to already be false.
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    const effectiveHasUnsavedChanges =
      pendingOverrideRef.current ?? hasUnsavedChanges;
    if (!effectiveHasUnsavedChanges) {
      executePendingAction();
    }
  }, [isModalOpen, hasUnsavedChanges, executePendingAction]);

  // Guard function for custom dismissal triggers
  const guard = useCallback(
    (action: () => void, hasUnsavedChangesOverride?: boolean): boolean => {
      const effectiveHasUnsavedChanges =
        hasUnsavedChangesOverride ?? hasUnsavedChanges;
      if (!effectiveHasUnsavedChanges) {
        return true;
      }

      triggerElementRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      pendingActionRef.current = action;
      pendingOverrideRef.current = hasUnsavedChangesOverride;
      setIsModalOpen(true);
      return false;
    },
    [hasUnsavedChanges]
  );

  // Handle save: save changes then execute pending action. Bounded by
  // SAVE_TIMEOUT_MS so a hung onSave() can't leave the modal stuck in the
  // isSaving state forever with no way for the user to escape it.
  const handleSave = useCallback(async () => {
    if (!onSave) {
      return;
    }
    setSaveError(null);
    setIsSaving(true);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new SaveTimeoutError()),
        SAVE_TIMEOUT_MS
      );
    });
    try {
      await Promise.race([onSave(), timeout]);
      setIsSaving(false);
      executePendingAction();
    } catch (err) {
      // Keep modal open on save failure — user can retry or discard
      if (err instanceof SaveTimeoutError) {
        logger.error('useSlideoutDismissGuard: save timed out (exceeded ~30s)');
      } else {
        logger.error(
          'useSlideoutDismissGuard: save failed',
          err instanceof Error ? err : new Error(String(err))
        );
      }
      setSaveError("We couldn't save your changes. Try again.");
      setIsSaving(false);
    } finally {
      clearTimeout(timeoutId);
    }
  }, [onSave, executePendingAction]);

  // Handle discard: discard changes and execute pending action
  const handleDiscard = useCallback(() => {
    setSaveError(null);
    onDiscard?.();
    executePendingAction();
  }, [onDiscard, executePendingAction]);

  // Handle cancel: close modal and clear pending action
  const handleCancel = useCallback(() => {
    setSaveError(null);
    pendingActionRef.current = null;
    pendingOverrideRef.current = undefined;
    restoreFocusTargetRef.current = triggerElementRef.current;
    triggerElementRef.current = null;
    setIsModalOpen(false);
  }, []);

  // Deterministically restores focus to the triggering control once the modal
  // has actually unmounted. Runs in a plain effect (not layout) so it fires
  // after react-aria's own unmount-time focus restoration and the slideout's
  // FocusScope containment have both already settled — whichever of them
  // "wins" the race, this runs last and wins for real.
  //
  // On Discard/Save the pending action usually closes the slideout too, and
  // the trigger is detached by the time this runs; focusing a disconnected
  // node is a no-op, leaving activeElement on <body> so the slideout's own
  // FocusScope restores focus to whatever opened it.
  useEffect(() => {
    if (isModalOpen) {
      return;
    }
    const target = restoreFocusTargetRef.current;
    if (!target) {
      return;
    }
    restoreFocusTargetRef.current = null;
    target.focus();
  }, [isModalOpen]);

  const modalProps: UnsavedChangesModalProps = useMemo(
    () => ({
      isOpen: isModalOpen,
      onSave: onSave ? handleSave : null,
      onDiscard: handleDiscard,
      onCancel: handleCancel,
      isSaving,
      saveError,
    }),
    [
      isModalOpen,
      onSave,
      handleSave,
      handleDiscard,
      handleCancel,
      isSaving,
      saveError,
    ]
  );

  return {
    guard,
    modalProps,
    isBlocking: isModalOpen,
  };
}
